import { getS3URL } from "@/_helpers";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const stats = [
    { value: "500+", label: "Courses" },
    { value: "50K+", label: "Students" },
    { value: "95%",  label: "Success Rate" },
];

function CourseBanner() {
    const navigate = useNavigate();
    const { courses, isFetching } = useSelector(state => state?.course || {});
    const courseList = courses?.all?.courses || [];

    const maxEnrollmentCourse = courseList.reduce(
        (max, course) => (course.enrollmentCount > (max?.enrollmentCount || 0) ? course : max),
        null
    );

    const featuredImage = maxEnrollmentCourse?.imageFiles?.[0]?.fileName;
    const fallbackImage = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=600&fit=crop";

    const formatFee = (fee) => {
        if (!fee && fee !== 0) return null;
        return fee === 0 ? "Free" : `$${Number(fee).toLocaleString()}`;
    };

    return (
        <div className="relative min-h-screen overflow-hidden" style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #e8eeff 50%, #f5f7ff 100%)" }}>

            <div className="relative z-10 container mx-auto px-6 lg:px-16 pt-20 pb-28">
                <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[80vh]">

                    {/* ── Left Content ── */}
                    <div className="space-y-8">

                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-blue-200 rounded-full shadow-sm">
                            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                            <span className="text-blue-700 text-sm font-semibold">10,000+ Students Enrolled</span>
                        </div>

                        {/* Heading */}
                        <div className="space-y-3">
                            <h1 className="text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight" style={{ color: "#1a3560" }}>
                                Master
                                <span className="block" style={{ color: "#264b82" }}>Digital Skills</span>
                                <span className="block text-4xl lg:text-5xl font-light" style={{ color: "#64748b" }}>
                                    of Tomorrow
                                </span>
                            </h1>
                            <p className="text-lg leading-relaxed max-w-md" style={{ color: "#475569" }}>
                                Join the future of learning with interactive courses, expert mentors,
                                and real-world projects that transform careers.
                            </p>
                        </div>

                        {/* CTA */}
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate("/courses")}
                                className="px-8 py-3.5 font-semibold rounded-xl text-white shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                                style={{ background: "linear-gradient(135deg, #264b82 0%, #1e40af 100%)" }}
                            >
                                Start Learning Now
                            </button>
                            <button
                                onClick={() => navigate("/courses")}
                                className="px-6 py-3.5 font-semibold rounded-xl border transition-all duration-200 hover:-translate-y-0.5"
                                style={{ borderColor: "#264b82", color: "#264b82", background: "transparent" }}
                            >
                                Browse Courses
                            </button>
                        </div>
                    </div>

                    {/* ── Right: Featured Course Card ── */}
                    <div
                        className="relative cursor-pointer"
                        onClick={() => maxEnrollmentCourse?.ID && navigate(`/categories/${maxEnrollmentCourse.ID}`)}
                    >

                        <div className="relative bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">

                            {/* Card Header */}
                            <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
                                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                                    Featured Course
                                </span>
                                <span
                                    className="text-xs font-semibold px-3 py-1 rounded-full"
                                    style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}
                                >
                                    Most Popular
                                </span>
                            </div>

                            {/* Course Image */}
                            <div className="px-5 pt-4">
                                {isFetching ? (
                                    <div className="w-full h-40 bg-slate-100 animate-pulse rounded-xl" />
                                ) : (
                                    <img
                                        src={featuredImage ? getS3URL(featuredImage) : fallbackImage}
                                        alt={maxEnrollmentCourse?.CourseName || "Featured Course"}
                                        className="w-full h-40 object-cover rounded-xl"
                                        onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
                                    />
                                )}
                            </div>

                            {/* Course Info */}
                            <div className="px-5 py-4 space-y-2.5">
                                {isFetching ? (
                                    <div className="space-y-2">
                                        <div className="h-4 bg-slate-100 animate-pulse rounded w-3/4" />
                                        <div className="h-3 bg-slate-100 animate-pulse rounded w-full" />
                                        <div className="h-3 bg-slate-100 animate-pulse rounded w-5/6" />
                                    </div>
                                ) : (
                                    <>
                                        <h3 className="font-bold text-sm leading-snug" style={{ color: "#1e293b" }}>
                                            {maxEnrollmentCourse?.CourseName}
                                        </h3>
                                        <p className="text-xs leading-relaxed line-clamp-2" style={{ color: "#64748b" }}>
                                            {maxEnrollmentCourse?.Description || ""}
                                        </p>
                                        <div className="flex items-center justify-between pt-1">
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-full flex-shrink-0" style={{ background: "linear-gradient(135deg, #60a5fa, #6366f1)" }} />
                                                <span className="text-xs" style={{ color: "#475569" }}>
                                                    {maxEnrollmentCourse?.instructorName}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {maxEnrollmentCourse?.enrollmentCount > 0 && (
                                                    <span className="text-xs" style={{ color: "#94a3b8" }}>
                                                        {maxEnrollmentCourse.enrollmentCount.toLocaleString()} enrolled
                                                    </span>
                                                )}
                                                {formatFee(maxEnrollmentCourse?.EnrollmentFee) && (
                                                    <span className="font-bold text-sm" style={{ color: "#264b82" }}>
                                                        {formatFee(maxEnrollmentCourse?.EnrollmentFee)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Stats Row */}
                            <div className="grid grid-cols-3 divide-x border-t" style={{ borderColor: "#f1f5f9", divideColor: "#f1f5f9" }}>
                                {stats.map(({ value, label }, i) => (
                                    <div
                                        key={label}
                                        className="flex flex-col items-center py-4 gap-0.5"
                                        style={{ background: i === 1 ? "#f8faff" : "#fafbff" }}
                                    >
                                        <span className="text-xl font-black" style={{ color: "#264b82" }}>{value}</span>
                                        <span className="text-xs font-medium" style={{ color: "#94a3b8" }}>{label}</span>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default CourseBanner;
